/**
 * Task Form Modal Component
 * Create/Edit task form in a dialog
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useCreateTask, useUpdateTask } from '../hooks/useTasks';
import type {
  Task,
  TaskStatus,
  TaskPriority,
  CreateTaskInput,
  UpdateTaskInput,
} from '../types';
import { TASK_STATUS_LABELS, TASK_PRIORITY_LABELS } from '../types';
import type { QuoteOption } from '../api';

interface TaskFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: Task;
  defaultStatus?: TaskStatus;
  quotes?: QuoteOption[];
  assignees?: Array<{ id: string; name: string; email?: string }>;
  canAssignTasks?: boolean;
}

interface FormValues {
  title: string;
  description: string;
  quote_id: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigned_to_id: string;
  due_date: string;
  estimated_hours: string;
  tags: string;
}

export function TaskFormModal({
  open,
  onOpenChange,
  task,
  defaultStatus = 'todo',
  quotes = [],
  assignees = [],
  canAssignTasks = false,
}: TaskFormModalProps) {
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const [quoteComboboxOpen, setQuoteComboboxOpen] = useState(false);

  const isEditing = !!task;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: task?.title || '',
      description: task?.description || '',
      quote_id: task?.quote_id || '',
      status: task?.status || defaultStatus,
      priority: task?.priority || 'medium',
      assigned_to_id: task?.assigned_to_id || '',
      due_date: task?.due_date || '',
      estimated_hours: task?.estimated_hours?.toString() || '',
      tags: task?.tags?.join(', ') || '',
    },
  });

  const selectedAssigneeId = watch('assigned_to_id');

  const onSubmit = async (data: FormValues) => {
    const selectedAssignee = canAssignTasks
      ? assignees.find((a) => a.id === data.assigned_to_id)
      : undefined;
    const assignmentFields = canAssignTasks
      ? {
          assigned_to_id: data.assigned_to_id || undefined,
          assigned_to_name: selectedAssignee?.name,
          assigned_to_email: selectedAssignee?.email,
        }
      : {};

    if (isEditing && task) {
      const input: UpdateTaskInput = {
        title: data.title,
        description: data.description || undefined,
        quote_id: data.quote_id || undefined,
        status: data.status,
        priority: data.priority,
        ...assignmentFields,
        due_date: data.due_date || undefined,
        estimated_hours: data.estimated_hours ? parseFloat(data.estimated_hours) : undefined,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      await updateTask.mutateAsync({ id: task.id, input });
    } else {
      const input: CreateTaskInput = {
        title: data.title,
        description: data.description || undefined,
        quote_id: data.quote_id || undefined,
        status: data.status,
        priority: data.priority,
        ...assignmentFields,
        due_date: data.due_date || undefined,
        estimated_hours: data.estimated_hours ? parseFloat(data.estimated_hours) : undefined,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      await createTask.mutateAsync(input);
    }

    reset();
    onOpenChange(false);
  };

  const isPending = createTask.isPending || updateTask.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Task' : 'Create Task'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="Task title"
              {...register('title', { required: 'Title is required' })}
            />
            {errors.title && (
              <p className="text-xs text-destructive">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Task description"
              rows={3}
              {...register('description')}
            />
          </div>

          {/* Status & Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value as TaskStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TASK_STATUS_LABELS) as TaskStatus[]).map((status) => (
                    <SelectItem key={status} value={status}>
                      {TASK_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={watch('priority')}
                onValueChange={(value) => setValue('priority', value as TaskPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[]).map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {TASK_PRIORITY_LABELS[priority]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quote */}
          {quotes.length > 0 && (
            <div className="space-y-2">
              <Label>Quote</Label>
              <Popover open={quoteComboboxOpen} onOpenChange={setQuoteComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={quoteComboboxOpen}
                    className="w-full justify-between font-normal"
                  >
                    {watch('quote_id')
                      ? (() => {
                          const selected = quotes.find((q) => q.id === watch('quote_id'));
                          if (!selected) return 'Select quote';
                          return selected.quote_number;
                        })()
                      : 'No quote'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search quotes..." />
                    <CommandList>
                      <CommandEmpty>No quote found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="no-quote"
                          onSelect={() => {
                            setValue('quote_id', '');
                            setQuoteComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              !watch('quote_id') ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          No quote
                        </CommandItem>
                        {quotes.map((quote) => (
                          <CommandItem
                            key={quote.id}
                            value={`${quote.quote_number} ${quote.account_name || ''} ${quote.project_name || ''}`}
                            onSelect={() => {
                              setValue('quote_id', quote.id);
                              setQuoteComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                watch('quote_id') === quote.id ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <div className="flex flex-col">
                              <span>
                                {quote.quote_number}
                              </span>
                              {(quote.account_name || quote.project_name) && (
                                <span className="text-xs text-muted-foreground">
                                  {quote.account_name || quote.project_name}
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Assignee */}
          {assignees.length > 0 && (
            <div className="space-y-2">
              <Label>Assignee</Label>
              <Select
                value={selectedAssigneeId || 'unassigned'}
                onValueChange={(value) => setValue('assigned_to_id', value === 'unassigned' ? '' : value)}
                disabled={!canAssignTasks}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {assignees.map((assignee) => (
                    <SelectItem key={assignee.id} value={assignee.id}>
                      {assignee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!canAssignTasks && (
                <p className="text-xs text-muted-foreground">
                  You don't have permission to assign tasks
                </p>
              )}
            </div>
          )}

          {/* Due Date & Estimated Hours Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date</Label>
              <Input id="due_date" type="date" {...register('due_date')} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated_hours">Estimated Hours</Label>
              <Input
                id="estimated_hours"
                type="number"
                step="0.5"
                min="0"
                placeholder="0"
                {...register('estimated_hours')}
              />
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              placeholder="Enter tags separated by commas"
              {...register('tags')}
            />
            <p className="text-xs text-muted-foreground">
              Separate tags with commas (e.g., "urgent, frontend, bug")
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
